import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Button, Textarea, HelperText } from '../components/ui';
import { ComputedBadges } from '../components/ComputedBadges';
import { computeInboxBadges } from '../lib/computedBadges';
import { PageHeader } from '../components/PageHeader';
import { Clock, X, ArrowRight, Upload, ExternalLink, Sparkles } from 'lucide-react';
import { inboxItemFingerprint } from '@scholarship-app/shared';
import { humanize } from '../lib/humanize';
import { normalizeToIso } from '../lib/dates';
import { parseAiDiscoveryPayload } from '../lib/aiDiscoveryImport';
import { DiscoveryPromptPanel } from '../components/DiscoveryPromptPanel';
import type { InboxItem } from '@scholarship-app/types';

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function InboxPage() {
  const { user, membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  // v0.2.50: the "Generate discovery prompt" panel (companion to the importer).
  const [showPrompt, setShowPrompt] = useState(false);
  const [importText, setImportText] = useState('');
  const [importStatus, setImportStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !membership || !applicantId) return;
    // Filter to inbox items tagged for the currently-active applicant. This
    // is what makes the header applicant switcher correctly partition the
    // inbox view across multiple applicants in a household.
    const colRef = collection(db, 'households', membership.householdId, 'inbox');
    const q = query(colRef, where('applicant_id', '==', applicantId));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => d.data() as InboxItem);
      // discovered_at is a Firestore Timestamp object (from serverTimestamp()),
      // not a string. Use toMillis() to compare. Newly-written items may have
      // a null/pending timestamp briefly — treat those as newest (Infinity).
      const toMs = (v: unknown): number => {
        if (!v) return 0;
        if (typeof v === 'object' && v !== null && 'toMillis' in v && typeof (v as { toMillis: unknown }).toMillis === 'function') {
          return (v as { toMillis: () => number }).toMillis();
        }
        if (typeof v === 'string') return new Date(v).getTime() || 0;
        return 0;
      };
      setItems(list.sort((a, b) => toMs(b.discovered_at) - toMs(a.discovered_at)));
      setLoading(false);
    });
    return () => unsub();
  }, [membership, applicantId]);

  async function promote(item: InboxItem) {
    if (!db || !membership || !applicantId || !user) return;
    const sid = genId('sch');
    const scholarshipRef = doc(db, 'households', membership.householdId, 'applicants', applicantId, 'scholarships', sid);
    const now = serverTimestamp();

    // The inbox item's deadline_approx is loose, human-readable text from the
    // extension's parsers (e.g. "April 30, 2026"). The scholarship's deadline
    // fields expect ISO date strings ("2026-04-30") or null — passing raw text
    // would later crash date formatting on the Scholarships/Dashboard pages.
    // Normalize here; if the text can't be confidently parsed, store null and
    // preserve the original text in deadline_notes so nothing is lost.
    const isoDeadline = normalizeToIso(item.deadline_approx);
    const deadlineUnparseable = !!item.deadline_approx && !isoDeadline;
    const deadlineNotes = deadlineUnparseable
      ? `Captured deadline: ${item.deadline_approx}`
      : '';

    await setDoc(scholarshipRef, {
      scholarship_id: sid,
      name: item.name,
      sponsor: item.sponsor,
      url: item.url,
      url_verified: item.url_verified,
      amount_min: item.amount_min,
      amount_max: item.amount_max,
      amount_type: item.amount_max != null && item.amount_min === item.amount_max ? 'fixed' : 'range',
      amount_notes: item.amount_notes,
      renewable: false,
      renewable_years: null,
      deadline: isoDeadline,
      deadline_rolling: item.deadline_rolling,
      linked_admission_deadline: null,
      effective_deadline: isoDeadline,
      deadline_notes: deadlineNotes,
      requires_admission_to: null,
      school_id: null,
      application_type: 'separate_application',
      school_tier_at_creation: null,
      status: 'identified',
      status_changed_at: now,
      submitted_at: null,
      result_announced_at: null,
      result: null,
      result_amount: null,
      requirements: {
        essay_required: false, essay_prompts: [], essay_word_counts: [],
        letters_of_rec_required: 0, letter_of_rec_restrictions: [],
        transcript_required: false, test_scores_required: false, min_test_scores: {},
        portfolio_required: false, headshot_required: false, interview_required: false,
        other: item.requirements_summary,
      },
      tags: item.tags,
      competitiveness: item.competitiveness,
      competitiveness_notes: '',
      need_based: item.need_based,
      homeschool_friendly: item.homeschool_friendly,
      demographic_restrictions: [],
      income_max: null,
      income_min: null,
      fit_score: null,
      fit_score_breakdown: null,
      effort_estimate: item.effort,
      priority_score: null,
      priority_score_computed_at: null,
      source_discovered_via: item.source,
      discovery_run_id: item.discovery_run_id,
      discovered_at: now,
      applied_with_essay_version_ids: [],
      linked_documents: [],
      linked_rec_request_ids: [],
      notes: item.notes ?? '',
      starred: false,
      snooze_until: null,
      created_at: now,
      updated_at: now,
      created_by: user.uid,
      updated_by: user.uid,
      archived: false,
    });
    await updateDoc(doc(db, 'households', membership.householdId, 'inbox', item.item_id), {
      status: 'promoted',
      promoted_to_scholarship_id: sid,
      reviewed_at: now,
      reviewed_by: user.uid,
    });
  }

  async function snooze(item: InboxItem) {
    if (!db || !membership || !user) return;
    const until = new Date();
    until.setDate(until.getDate() + 30);
    await updateDoc(doc(db, 'households', membership.householdId, 'inbox', item.item_id), {
      status: 'snoozed',
      snooze_until: until.toISOString(),
      reviewed_at: serverTimestamp(),
      reviewed_by: user.uid,
    });
  }

  async function dismiss(item: InboxItem) {
    if (!db || !membership || !user) return;
    const fingerprint = item.fingerprint || inboxItemFingerprint(item.name, item.sponsor);
    await setDoc(doc(db, 'households', membership.householdId, 'dismissed', fingerprint), {
      fingerprint,
      name: item.name,
      sponsor: item.sponsor,
      url: item.url,
      dismissed_at: serverTimestamp(),
      dismissed_by: user.uid,
      reason: '',
      original_inbox_item_id: item.item_id,
    });
    await updateDoc(doc(db, 'households', membership.householdId, 'inbox', item.item_id), {
      status: 'dismissed',
      reviewed_at: serverTimestamp(),
      reviewed_by: user.uid,
    });
  }

  async function importAiJson() {
    if (!db || !membership || !applicantId || !user) return;
    setImportStatus(null);
    try {
      // v0.2.50: tolerant parse — strips ```json fences / stray prose, fills
      // defaults for missing optional fields, drops items with no name/sponsor.
      const run = parseAiDiscoveryPayload(importText);

      const existingSnap = await getDocs(collection(db, 'households', membership.householdId, 'inbox'));
      const existingFingerprints = new Set(existingSnap.docs.map((d) => (d.data() as InboxItem).fingerprint));
      const dismissedSnap = await getDocs(collection(db, 'households', membership.householdId, 'dismissed'));
      const dismissedFingerprints = new Set(dismissedSnap.docs.map((d) => d.id));

      const batch = writeBatch(db);
      let added = 0;
      let skipped = 0;
      const runId = `run_${run.runDate.replace(/-/g, '_')}_${Date.now()}`;
      for (const item of run.items) {
        const fp = inboxItemFingerprint(item.name, item.sponsor);
        if (existingFingerprints.has(fp) || dismissedFingerprints.has(fp)) {
          skipped++;
          continue;
        }
        // v0.2.50: also dedupe WITHIN this paste — an AI can list the same
        // award twice (e.g. once per tier) and both would otherwise be added.
        existingFingerprints.add(fp);
        const id = genId('inbox');
        batch.set(doc(db, 'households', membership.householdId, 'inbox', id), {
          item_id: id,
          applicant_id: applicantId,
          source: 'ai_discovery_import',
          discovery_run_id: runId,
          name: item.name,
          sponsor: item.sponsor,
          url: item.url,
          url_verified: item.url_verified,
          amount_min: item.amount_min,
          amount_max: item.amount_max,
          amount_notes: item.amount_notes,
          deadline_approx: item.deadline_approx,
          deadline_rolling: item.deadline_rolling,
          match_strength: item.match_strength,
          why_match: item.why_match,
          competitiveness: item.competitiveness,
          effort: item.effort,
          tags: item.tags,
          requirements_summary: item.requirements_summary,
          need_based: item.need_based,
          homeschool_friendly: item.homeschool_friendly,
          fingerprint: fp,
          status: 'new',
          snooze_until: null,
          promoted_to_scholarship_id: null,
          discovered_at: serverTimestamp(),
          reviewed_at: null,
          reviewed_by: null,
          notes: item.notes ?? null,
        });
        added++;
      }
      await batch.commit();
      const parts = [
        `Imported ${added} new item${added === 1 ? '' : 's'}`,
        `skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}`,
      ];
      if (run.malformed > 0) {
        parts.push(`dropped ${run.malformed} incomplete item${run.malformed === 1 ? '' : 's'} (no name or sponsor)`);
      }
      setImportStatus(parts.join('; ') + '.');
      setImportText('');
    } catch (e) {
      setImportStatus('Error: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  const visible = items.filter((i) => i.status === 'new' || i.status === 'reviewing');

  // Everything still sitting in the inbox (incl. snoozed) is "known" — the
  // discovery prompt tells the AI not to return these again. Promoted items
  // are covered by the tracked-scholarships list; dismissed by the dismissed
  // collection.
  const pendingForPrompt = useMemo(
    () => items
      .filter((i) => i.status === 'new' || i.status === 'reviewing' || i.status === 'snoozed')
      .map((i) => ({ name: i.name, sponsor: i.sponsor })),
    [items]
  );

  if (loading) return <div className="text-soft">Loading inbox…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inbox"
        description={`${visible.length} pending review`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" onClick={() => setShowPrompt(!showPrompt)}>
              <Sparkles size={16} className="mr-1" /> Generate discovery prompt
            </Button>
            <Button onClick={() => setShowImport(!showImport)}>
              <Upload size={16} className="mr-1" /> Import AI Discoveries
            </Button>
          </div>
        }
      />

      {showPrompt && (
        <DiscoveryPromptPanel
          pendingInbox={pendingForPrompt}
          onOpenImport={() => setShowImport(true)}
          onClose={() => setShowPrompt(false)}
        />
      )}

      {showImport && (
        <Card className="p-4 space-y-3">
          <h2 className="font-medium">Paste the JSON your AI returned</h2>
          <HelperText>
            Use <span className="font-medium">Generate discovery prompt</span> above, run it in your own AI, and paste
            its JSON answer here. Extra text or a ```json fence around it is fine.
          </HelperText>
          <Textarea rows={8} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder='{"ai_discovery_run": {...}}' />
          {importStatus && <p className={`text-sm ${importStatus.startsWith('Error') ? 'text-rose-600' : 'text-emerald-700'}`}>{importStatus}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowImport(false); setImportText(''); setImportStatus(null); }}>Close</Button>
            <Button onClick={importAiJson} disabled={!importText.trim()}>Import</Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {visible.length === 0 ? (
          <Card className="p-6 text-center text-soft">
            Inbox is empty.
          </Card>
        ) : (
          visible.map((item) => (
            <Card key={item.item_id} className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-ink">{item.name}</h3>
                    <Badge variant={item.match_strength === 'high' ? 'green' : item.match_strength === 'medium' ? 'amber' : 'gray'}>
                      {humanize(item.match_strength)} Match
                    </Badge>
                    {item.effort && <Badge>Effort: {humanize(item.effort)}</Badge>}
                    {item.competitiveness && <Badge variant="gray">{humanize(item.competitiveness)} Competition</Badge>}
                  </div>
                  <p className="text-sm text-soft mt-0.5">{item.sponsor}</p>
                  <p className="text-sm text-soft mt-2">{item.why_match}</p>
                  {item.notes && <p className="text-xs text-soft mt-2 italic">{item.notes}</p>}
                  <div className="mt-2 flex items-center gap-3 text-xs text-soft flex-wrap">
                    {typeof item.amount_max === 'number' && item.amount_max > 0 && (
                      <span>${(item.amount_min ?? 0).toLocaleString()}–${item.amount_max.toLocaleString()}</span>
                    )}
                    {item.deadline_approx && <span>Due {item.deadline_approx}</span>}
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline flex items-center gap-1">
                        <ExternalLink size={11} /> open
                      </a>
                    )}
                    {item.url_verified === false && <Badge variant="amber">URL not verified</Badge>}
                  </div>

                  {/* v0.2.31: computed badges for the inbox item. */}
                  <div className="mt-2">
                    <ComputedBadges badges={computeInboxBadges(item)} size="xs" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => promote(item)}>
                    <ArrowRight size={12} className="mr-1" /> Promote
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => snooze(item)}>
                    <Clock size={12} className="mr-1" /> Snooze 30d
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(item)}>
                    <X size={12} className="mr-1" /> Dismiss
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
